(() => {
  const SECTION_SELECTOR = '[data-pd-testimonials]';
  const CARD_SELECTOR = '.pd-testimonial-card';
  const VIDEO_SELECTOR = '.pd-testimonial-card__video';

  const initializedSections = new WeakSet();

  const getVideo = (card) => {
    return card?.querySelector(VIDEO_SELECTOR) || null;
  };

  const refreshSectionState = (section) => {
    if (!section) return;

    const hasActiveVideo = Boolean(
      section.querySelector(
        `${CARD_SELECTOR}.is-video-active`
      )
    );

    section.classList.toggle(
      'has-active-video',
      hasActiveVideo
    );
  };

  const setCardActive = (card, active) => {
    if (!card) return;

    card.classList.toggle(
      'is-video-active',
      active
    );

    refreshSectionState(
      card.closest(SECTION_SELECTOR)
    );
  };

  const setPlayingState = (card, playing) => {
    if (!card) return;

    card.classList.toggle(
      'is-video-playing',
      playing
    );

    const button = card.querySelector(
      '[data-pd-play]'
    );

    if (button) {
      button.setAttribute(
        'aria-pressed',
        String(playing)
      );

      button.setAttribute(
        'aria-label',
        playing
          ? 'Pausar video'
          : 'Reproducir video con sonido'
      );
    }
  };

  const setMutedState = (card, muted) => {
    const video = getVideo(card);

    if (!video) return;

    video.muted = muted;
    video.defaultMuted = muted;

    if (muted) {
      video.setAttribute('muted', '');
    } else {
      video.removeAttribute('muted');
    }

    card.classList.toggle(
      'is-video-muted',
      muted
    );

    const button = card.querySelector(
      '[data-pd-mute]'
    );

    if (button) {
      button.setAttribute(
        'aria-pressed',
        String(!muted)
      );

      button.setAttribute(
        'aria-label',
        muted
          ? 'Activar audio'
          : 'Silenciar video'
      );

      button.setAttribute(
        'title',
        muted
          ? 'Activar audio'
          : 'Silenciar video'
      );
    }
  };

  const safePlay = (video) => {
    const promise = video.play();

    if (
      promise &&
      typeof promise.catch === 'function'
    ) {
      return promise;
    }

    return Promise.resolve();
  };

  const resetCardToPreview = (
    card,
    restart = true
  ) => {
    const video = getVideo(card);

    if (!video) return;

    video.pause();
    video.loop = true;

    setMutedState(card, true);
    setPlayingState(card, false);
    setCardActive(card, false);

    if (restart) {
      try {
        video.currentTime = 0;
      } catch (error) {
        /*
         * Los metadatos del video podrían
         * no estar disponibles todavía.
         */
      }
    }

    safePlay(video).catch(() => {
      /*
       * El autoplay silencioso puede ser
       * bloqueado en algunos navegadores.
       */
    });
  };

  const resetOtherCards = (currentCard) => {
    document
      .querySelectorAll(
        `${SECTION_SELECTOR} ${CARD_SELECTOR}.is-video-active`
      )
      .forEach((card) => {
        if (card !== currentCard) {
          resetCardToPreview(card);
        }
      });
  };

  const activateCard = (
    card,
    options = {}
  ) => {
    const video = getVideo(card);

    if (!video) {
      return Promise.resolve();
    }

    const {
      restart = false,
      unmute = true
    } = options;

    resetOtherCards(card);
    setCardActive(card, true);

    video.loop = false;

    if (restart) {
      try {
        video.currentTime = 0;
      } catch (error) {
        /*
         * Los metadatos todavía podrían
         * no estar cargados.
         */
      }
    }

    if (unmute) {
      setMutedState(card, false);
    }

    const playPromise = safePlay(video);

    playPromise
      .then(() => {
        setPlayingState(card, true);
      })
      .catch(() => {
        resetCardToPreview(card, false);
      });

    return playPromise;
  };

  const togglePlay = (card) => {
    const video = getVideo(card);

    if (!video) return;

    if (
      !card.classList.contains(
        'is-video-active'
      )
    ) {
      activateCard(card, {
        restart: true,
        unmute: true
      });

      return;
    }

    if (video.paused || video.ended) {
      safePlay(video)
        .then(() => {
          setPlayingState(card, true);
        })
        .catch(() => {
          setPlayingState(card, false);
        });
    } else {
      video.pause();
      setPlayingState(card, false);
    }
  };

  const toggleMute = (card) => {
    const video = getVideo(card);

    if (!video) return;

    if (video.muted) {
      if (
        !card.classList.contains(
          'is-video-active'
        )
      ) {
        activateCard(card, {
          restart: false,
          unmute: true
        });
      } else {
        setMutedState(card, false);

        if (video.paused) {
          safePlay(video)
            .then(() => {
              setPlayingState(card, true);
            })
            .catch(() => {
              setPlayingState(card, false);
            });
        }
      }
    } else {
      setMutedState(card, true);
    }
  };

  const enterFullscreen = (card) => {
    const media = card?.querySelector(
      '.pd-testimonial-card__media'
    );

    const video = getVideo(card);

    if (!media || !video) return;

    /*
     * Comienza el video dentro de la misma
     * interacción realizada por el usuario.
     */
    if (
      !card.classList.contains(
        'is-video-active'
      )
    ) {
      activateCard(card, {
        restart: false,
        unmute: true
      });
    } else if (video.paused) {
      safePlay(video)
        .then(() => {
          setPlayingState(card, true);
        })
        .catch(() => {
          setPlayingState(card, false);
        });
    }

    /*
     * Navegadores modernos.
     */
    if (media.requestFullscreen) {
      media.requestFullscreen().catch(() => {});

      return;
    }

    /*
     * Safari con prefijo WebKit.
     */
    if (media.webkitRequestFullscreen) {
      media.webkitRequestFullscreen();

      return;
    }

    /*
     * Respaldo para versiones de iPhone
     * que usan el reproductor nativo.
     */
    if (video.webkitEnterFullscreen) {
      video.controls = true;

      video.addEventListener(
        'webkitendfullscreen',
        () => {
          video.controls = false;
        },
        {
          once: true
        }
      );

      video.webkitEnterFullscreen();
    }
  };

  const getMobileCards = (section) => {
    return Array.from(
      section.querySelectorAll(
        `
          .pd-testimonials__group:first-child
          .pd-testimonial-card
        `
      )
    );
  };

  const getClosestSlideIndex = (section) => {
    const viewport = section.querySelector(
      '[data-pd-viewport]'
    );

    const cards = getMobileCards(section);

    if (!viewport || !cards.length) {
      return 0;
    }

    const viewportRect =
      viewport.getBoundingClientRect();

    const viewportCenter =
      viewportRect.left +
      viewportRect.width / 2;

    let closestIndex = 0;
    let closestDistance =
      Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const cardRect =
        card.getBoundingClientRect();

      const cardCenter =
        cardRect.left +
        cardRect.width / 2;

      const distance = Math.abs(
        cardCenter - viewportCenter
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  const updateMobileNavigation = (
    section,
    index
  ) => {
    const cards = getMobileCards(section);

    if (!cards.length) return;

    const safeIndex = Math.max(
      0,
      Math.min(index, cards.length - 1)
    );

    section.dataset.pdActiveSlide =
      String(safeIndex);

    section
      .querySelectorAll('[data-pd-dot]')
      .forEach((dot) => {
        const isActive =
          Number(dot.dataset.pdDot) ===
          safeIndex;

        dot.classList.toggle(
          'is-active',
          isActive
        );

        dot.setAttribute(
          'aria-current',
          isActive ? 'true' : 'false'
        );
      });

    const previousButton =
      section.querySelector(
        '[data-pd-prev]'
      );

    const nextButton =
      section.querySelector(
        '[data-pd-next]'
      );

    if (previousButton) {
      previousButton.disabled =
        safeIndex === 0;
    }

    if (nextButton) {
      nextButton.disabled =
        safeIndex === cards.length - 1;
    }
  };

  const scrollToSlide = (
    section,
    index,
    behavior = 'smooth'
  ) => {
    const viewport = section.querySelector(
      '[data-pd-viewport]'
    );

    const cards = getMobileCards(section);

    if (!viewport || !cards.length) return;

    const safeIndex = Math.max(
      0,
      Math.min(index, cards.length - 1)
    );

    const card = cards[safeIndex];

    const viewportRect =
      viewport.getBoundingClientRect();

    const cardRect =
      card.getBoundingClientRect();

    /*
     * Centra completamente la tarjeta.
     * Esto evita que el primer video aparezca
     * parcialmente fuera de la pantalla.
     */
    const targetLeft =
      viewport.scrollLeft +
      cardRect.left -
      viewportRect.left -
      (
        viewportRect.width -
        cardRect.width
      ) / 2;

    viewport.scrollTo({
      left: targetLeft,
      behavior
    });

    updateMobileNavigation(
      section,
      safeIndex
    );
  };

  const initializeVideo = (video) => {
    if (
      video.dataset.pdVideoInitialized ===
      'true'
    ) {
      return;
    }

    video.dataset.pdVideoInitialized =
      'true';

    const card = video.closest(
      CARD_SELECTOR
    );

    if (!card) return;

    setMutedState(card, true);
    setPlayingState(card, false);

    video.addEventListener('play', () => {
      if (
        card.classList.contains(
          'is-video-active'
        )
      ) {
        setPlayingState(card, true);
      }
    });

    video.addEventListener('pause', () => {
      if (
        card.classList.contains(
          'is-video-active'
        )
      ) {
        setPlayingState(card, false);
      }
    });

    video.addEventListener(
      'volumechange',
      () => {
        card.classList.toggle(
          'is-video-muted',
          video.muted
        );

        const button = card.querySelector(
          '[data-pd-mute]'
        );

        if (button) {
          button.setAttribute(
            'aria-pressed',
            String(!video.muted)
          );

          button.setAttribute(
            'aria-label',
            video.muted
              ? 'Activar audio'
              : 'Silenciar video'
          );

          button.setAttribute(
            'title',
            video.muted
              ? 'Activar audio'
              : 'Silenciar video'
          );
        }
      }
    );

    video.addEventListener('ended', () => {
      if (
        card.classList.contains(
          'is-video-active'
        )
      ) {
        resetCardToPreview(card);
      }
    });
  };

  const initializeSection = (section) => {
    if (
      !section ||
      initializedSections.has(section)
    ) {
      return;
    }

    initializedSections.add(section);

    section
      .querySelectorAll(VIDEO_SELECTOR)
      .forEach(initializeVideo);

    const viewport = section.querySelector(
      '[data-pd-viewport]'
    );

    if (!viewport) return;

    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;

      window.requestAnimationFrame(() => {
        updateMobileNavigation(
          section,
          getClosestSlideIndex(section)
        );

        ticking = false;
      });
    };

    viewport.addEventListener(
      'scroll',
      handleScroll,
      {
        passive: true
      }
    );

    updateMobileNavigation(section, 0);

    /*
     * Conserva la tarjeta seleccionada y
     * centrada cuando cambia el ancho.
     */
    if (window.ResizeObserver) {
      const observer = new ResizeObserver(
        () => {
          const activeIndex = Number(
            section.dataset.pdActiveSlide || 0
          );

          scrollToSlide(
            section,
            activeIndex,
            'auto'
          );
        }
      );

      observer.observe(viewport);
    }
  };

  const initializeAllSections = (
    root = document
  ) => {
    if (
      root.matches?.(SECTION_SELECTOR)
    ) {
      initializeSection(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(initializeSection);
  };

  document.addEventListener(
    'click',
    (event) => {
      const playButton =
        event.target.closest(
          '[data-pd-play]'
        );

      if (playButton) {
        event.preventDefault();
        event.stopPropagation();

        togglePlay(
          playButton.closest(CARD_SELECTOR)
        );

        return;
      }

      const muteButton =
        event.target.closest(
          '[data-pd-mute]'
        );

      if (muteButton) {
        event.preventDefault();
        event.stopPropagation();

        toggleMute(
          muteButton.closest(CARD_SELECTOR)
        );

        return;
      }

      const fullscreenButton =
        event.target.closest(
          '[data-pd-fullscreen]'
        );

      if (fullscreenButton) {
        event.preventDefault();
        event.stopPropagation();

        enterFullscreen(
          fullscreenButton.closest(
            CARD_SELECTOR
          )
        );

        return;
      }

      const dot = event.target.closest(
        '[data-pd-dot]'
      );

      if (dot) {
        event.preventDefault();

        const section = dot.closest(
          SECTION_SELECTOR
        );

        scrollToSlide(
          section,
          Number(dot.dataset.pdDot)
        );

        return;
      }

      const previousButton =
        event.target.closest(
          '[data-pd-prev]'
        );

      if (previousButton) {
        event.preventDefault();

        const section =
          previousButton.closest(
            SECTION_SELECTOR
          );

        const currentIndex = Number(
          section.dataset.pdActiveSlide || 0
        );

        scrollToSlide(
          section,
          currentIndex - 1
        );

        return;
      }

      const nextButton =
        event.target.closest(
          '[data-pd-next]'
        );

      if (nextButton) {
        event.preventDefault();

        const section =
          nextButton.closest(
            SECTION_SELECTOR
          );

        const currentIndex = Number(
          section.dataset.pdActiveSlide || 0
        );

        scrollToSlide(
          section,
          currentIndex + 1
        );

        return;
      }

      /*
       * También permite reproducir al tocar
       * directamente cualquier zona del video.
       */
      const videoArea =
        event.target.closest(
          '[data-pd-video-area]'
        );

      if (
        videoArea &&
        !event.target.closest('a, button')
      ) {
        event.preventDefault();

        togglePlay(
          videoArea.closest(CARD_SELECTOR)
        );
      }
    }
  );

  document.addEventListener(
    'fullscreenchange',
    () => {
      document
        .querySelectorAll(
          '[data-pd-fullscreen]'
        )
        .forEach((button) => {
          const card = button.closest(
            CARD_SELECTOR
          );

          const media = card?.querySelector(
            '.pd-testimonial-card__media'
          );

          const isFullscreen =
            document.fullscreenElement === media;

          button.setAttribute(
            'aria-label',
            isFullscreen
              ? 'Salir de pantalla completa'
              : 'Ver en pantalla completa'
          );
        });
    }
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (!document.hidden) return;

      document
        .querySelectorAll(
          `
            ${SECTION_SELECTOR}
            ${CARD_SELECTOR}.is-video-active
          `
        )
        .forEach((card) => {
          resetCardToPreview(
            card,
            false
          );
        });
    }
  );

  /*
   * Shopify Theme Editor:
   * reinicializa una sección después
   * de cambiarla en el personalizador.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAllSections(event.target);
    }
  );

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const card =
        event.target.closest?.(
          CARD_SELECTOR
        );

      const section =
        event.target.closest?.(
          SECTION_SELECTOR
        );

      if (
        !card ||
        !section ||
        window.innerWidth > 749
      ) {
        return;
      }

      const cards =
        getMobileCards(section);

      const index = cards.indexOf(card);

      if (index >= 0) {
        scrollToSlide(section, index);
      }
    }
  );

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeAllSections();
      }
    );
  } else {
    initializeAllSections();
  }
})();